import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Store } from '@ngrx/store';
import { AuthService, RoutingService, StateUtils, StateWithProcess } from '@spartacus/core';
import { EMPTY, Observable, combineLatest, of } from 'rxjs';
import {
  catchError,
  delay,
  filter,
  map,
  share,
  shareReplay,
  startWith,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { UserIdService } from '../../auth';
import { ArticleService } from '../../catalog';
import { ArticleComparativePrice, ArticlePrice, ArticleRef, PRICE_LOADING_MAX_ATTEMPTS, Unit } from '../../model';
import { withdrawOn } from '../../util';
import { PriceFacade, PrincipalConfigurationService } from '../index';
import { UserActions } from '../store/actions';
import {
  getArticlePriceLoadingAttempts,
  getArticlePriceState,
  getArticlePriceStateSuccess,
} from '../store/selectors/price.selectors';
import { ArticlePriceState, StateWithUser } from '../store/user-state';

@Injectable()
export class PriceService implements PriceFacade {
  private delay$: Observable<number> = this.principalConfigurationService.getValue('articlePriceFetchInterval').pipe(
    map((getArticlePriceDelay) => (typeof getArticlePriceDelay?.value === 'number' ? getArticlePriceDelay?.value : 100)),
    startWith(100),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  constructor(
    private store: Store<StateWithUser & StateWithProcess<void>>,
    private articleService: ArticleService,
    private routingService: RoutingService,
    private userIdService: UserIdService,
    private principalConfigurationService: PrincipalConfigurationService,
    private authService: AuthService,
    @Inject(PLATFORM_ID) protected platformId: Object
  ) {}

  getArticlePrice(
    articleRef: ArticleRef,
    quantity: number,
    customerSelectedUnit?: Unit,
    async: boolean = true,
    forceLoadPrice: boolean = false
  ): Observable<ArticlePrice | undefined> {
    const eCommerceQuantity = !!customerSelectedUnit ? customerSelectedUnit.inEcommerceUnit * quantity : quantity;
    const enableDisplayOfPrices$ = this.principalConfigurationService.isEnabled('enableDisplayOfPrices').pipe(share());
    const enableOneTimeCustomers$ = this.principalConfigurationService.isEnabled('enableOneTimeCustomers').pipe(share());
    const isUserLoggedIn$ = this.authService.isUserLoggedIn().pipe(share());
    const articleLineComparativePricesMode$: Observable<string> = this.principalConfigurationService
      .getValue('articleLineComparativePricesMode')
      .pipe(
        map((principalConfiguration) =>
          typeof principalConfiguration?.value === 'string' ? principalConfiguration?.value : undefined
        ),
        share()
      );

    const isUserLoggedInAndEnableDisplayOfPrices$ = combineLatest([isUserLoggedIn$, enableDisplayOfPrices$]).pipe(
      map(([isUserLoggedIn, enableDisplayOfPrices]) => isUserLoggedIn && enableDisplayOfPrices)
    );

    return enableDisplayOfPrices$.pipe(
      filter(Boolean),
      switchMap(() => combineLatest([isUserLoggedIn$, enableOneTimeCustomers$])),
      filter(
        ([isUserLoggedIn, enableOneTimeCustomers]) =>
          isUserLoggedIn || (isPlatformBrowser(this.platformId) && enableOneTimeCustomers)
      ),
      switchMap(() => this.store.select(getArticlePriceState, { key: `${articleRef}|${eCommerceQuantity}` })),
      withLatestFrom(this.delay$),
      switchMap(([aps, time]) => {
        const value = aps?.value;
        const hasPrice = Object.keys(value?.price || {}).length;
        if (!forceLoadPrice && !hasPrice && value?.attempts > 0) {
          return of(aps).pipe(delay(time));
        }
        return of(aps);
      }),
      tap((aps) => {
        if (this.shouldLoad(aps, forceLoadPrice)) {
          this.loadPrice(articleRef, quantity, customerSelectedUnit, async);
        }
      }),
      map((aps) => aps.value),
      filter((value) => {
        if (forceLoadPrice) {
          return (!!value.price && !!Object.keys(value.price || {}).length) || value?.attempts >= 6;
        }
        return !!value?.price || value?.attempts > PRICE_LOADING_MAX_ATTEMPTS;
      }),
      map((value) => ({ ...value?.price, attempts: value?.attempts })),
      withLatestFrom(articleLineComparativePricesMode$),
      map(([articlePrice, articleLineComparativePricesMode]) => ({
        ...articlePrice,
        comparativePrice: this.getComparativePrice(articlePrice, articleLineComparativePricesMode),
      })),
      withdrawOn(
        isUserLoggedInAndEnableDisplayOfPrices$.pipe(
          filter((isUserLoggedInAndEnableDisplayOfPrices) => !isUserLoggedInAndEnableDisplayOfPrices)
        )
      ),
      take(1)
    );
  }

  loadingPrice(articleRef: ArticleRef, quantity: number, customerSelectedUnit?: Unit): Observable<boolean> {
    const eCommerceQuantity = !!customerSelectedUnit ? customerSelectedUnit.inEcommerceUnit * quantity : quantity;
    return this.store
      .select(getArticlePriceState, { key: `${articleRef}|${eCommerceQuantity}` })
      .pipe(map((state) => state?.loading || (state?.error && state?.value?.attempts < PRICE_LOADING_MAX_ATTEMPTS)));
  }

  loadedPrice(articleRef: ArticleRef, quantity: number, customerSelectedUnit?: Unit): Observable<boolean> {
    const eCommerceQuantity = !!customerSelectedUnit ? customerSelectedUnit.inEcommerceUnit * quantity : quantity;
    return this.store.select(getArticlePriceStateSuccess, { key: `${articleRef}|${eCommerceQuantity}` });
  }

  priceLoadingAttempts(articleRef: ArticleRef, quantity: number, customerSelectedUnit?: Unit): Observable<number> {
    const eCommerceQuantity = !!customerSelectedUnit ? customerSelectedUnit.inEcommerceUnit * quantity : quantity;
    return this.store.select(getArticlePriceLoadingAttempts, { key: `${articleRef}|${eCommerceQuantity}` });
  }

  loadPrice(articleRef: ArticleRef, quantity: number, customerSelectedUnit?: Unit, async: boolean = false): void {
    this.userIdService
      .takeUserId()
      .pipe(
        withLatestFrom(this.articleService.getArticle(articleRef)),
        tap(([userId, article]) => {
          if (!!article) {
            this.store.dispatch(
              new UserActions.LoadArticlePrice(
                {
                  articleNumber: articleRef,
                  quantity: !!customerSelectedUnit ? customerSelectedUnit.inEcommerceUnit * quantity : quantity,
                  unit: article.unit?.code,
                  userId,
                },
                async
              )
            );
          }
        }),
        catchError(() => {
          this.routingService.go({ cxRoute: 'login' });
          return EMPTY;
        })
      )
      .subscribe();
  }

  private shouldLoad(aps: StateUtils.LoaderState<ArticlePriceState>, forceLoadPrice: boolean) {
    if (aps.loading) {
      return false;
    }
    const value = aps.value;
    if (!!value) {
      if (!!value.lastUpdateTime && new Date(value.lastUpdateTime).toDateString() !== new Date().toDateString()) {
        return true;
      }
      const hasPrice = !!value.price && !!Object.keys(value.price || {}).length;
      if (forceLoadPrice && !hasPrice && value.attempts === PRICE_LOADING_MAX_ATTEMPTS) {
        return true;
      } else {
        return !!value.price ? false : value.attempts < PRICE_LOADING_MAX_ATTEMPTS;
      }
    }
    return forceLoadPrice ? false : true;
  }

  private getComparativePrice(articlePrice: ArticlePrice, mode: string): ArticleComparativePrice | undefined {
    return (
      articlePrice?.comparativePrices?.find((entry) => entry.code === (mode ?? 'rateAlt')) ??
      articlePrice?.comparativePrices?.find((entry) => entry.code === 'rate')
    );
  }
}
